import React, { useCallback, useEffect, useState } from 'react';
import IconExportExcel from '../../../components/Icons/IconExportExcel';
import { Form, Select, DatePicker, Button, ConfigProvider, Flex, Input, Table, Typography, Tooltip } from 'antd';
import type { TableProps } from 'antd';
import TutorialsVideo from '../../../components/TutorialsVideo';
import { handleDisplayTimeToNow, handleGetUrlGuide } from '../../../utils/helpers';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import ExcelJS from 'exceljs';
import iconSearch from '../../../assets/images/icons/icon-search.svg';
import iconAction from '../../../assets/images/ic-edit.svg';
import {
  getListOrdersPending,
  setCurrentPage,
  setFilter,
  setKeyword,
  setLoadingOrderPending,
  setPerPage,
} from '../../../redux/slice/orders/ordersPendingSlide';
import dayjs from 'dayjs';
import { Constant, USER_GROUP } from '../../../config/constant';
import { OrderItemParams } from '../../../types/order';
import moment from 'moment';
import { DEFAULT_PAGE_SIZE } from 'antd/es/table/hooks/usePagination';
import { toast } from 'react-toastify';
import { isArray } from 'lodash';
import '../styles/Order.scss';
import ModalOrderInfo from '../components/ModalOrderData';
import iconSwap from '../../../assets/images/orders/icon-swap.svg';
import iconInsurance from '../../../assets/images/orders/icon-insurance.svg';
import { columnsExcelPending, TabStatusOrderProcessed, TypeModalOrder } from '../models/constants';
import statusPending from '../../../assets/images/orders/status-pending.svg';
import { getListOrdersAPI, getOrderDetailAPI } from '../api';
import TextDisplay from '../../../components/Display/TextDisplay';
import logoCompanyDefault from '../../../assets/images/orders/logo-company-default.png';
import { handleGetGuestSource } from './OrderProcessed';
import logoNapas from '../../../assets/images/orders/logo-napas.png';
import iconWeb from '../../../assets/images/icon-web.svg';
import iconMobile from '../../../assets/images/icon-mobile.svg';

type FieldType = {
  date_filter: string;
  range_date_filter: string[];
  keyword: string;
};

const { RangePicker } = DatePicker;

const optionsDateFilter = [
  {
    value: 'depart_date',
    label: 'Theo ngày khởi hành',
  },
  {
    value: 'created_at',
    label: 'Theo ngày đặt vé',
  },
];

const OrderPending = () => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const [loadingExcel, setLoadingExcel] = useState<boolean>(false);
  const [orderData, setOrderData] = useState<OrderItemParams>({} as OrderItemParams);
  const [openModalOrderInfo, setOpenModalOrderInfo] = useState<boolean>(false);

  const listTutorials = useAppSelector((state) => state?.tutorials?.listTutorials);
  const accountUser = useAppSelector((state) => state.authAccount?.user);
  const { filter: listFilter, listOrder, pagination, loading } = useAppSelector((state) => state.ordersPending);

  const limit_pagination: any = window.localStorage.getItem('limit_pagination');
  const limitPagination = isNaN(Number(limit_pagination)) ? Constant.PAGE_SIZE : Number(limit_pagination);

  const systemConfig = useAppSelector((state) => state.systemConfig?.result);
  const disabledDaysDate = useCallback(
    (current: any, { from }: any) => {
      if (from) {
        return Math.abs(current.diff(from, 'days')) >= systemConfig?.export_report_month;
      }
      return false;
    },
    [systemConfig?.export_report_month],
  );

  const handleGetListOrdersPending = useCallback(async () => {
    await dispatch(
      getListOrdersPending({
        page: pagination?.current_page,
        keyword: listFilter?.keyword ? listFilter?.keyword : '',
        date_filter: listFilter?.date_filter,
        from: dayjs(listFilter?.from, 'YYYY-MM-DD', true).isValid()
          ? dayjs(listFilter?.from).format('YYYY-MM-DD')
          : undefined,
        to: dayjs(listFilter?.to, 'YYYY-MM-DD', true).isValid()
          ? dayjs(listFilter?.to).format('YYYY-MM-DD')
          : undefined,
        status: TabStatusOrderProcessed.UN_PROCESSED,
        limit: pagination?.per_page || limitPagination,
        is_full: '0',
        is_detail_booking: '0',
      }),
    );
  }, [
    dispatch,
    limitPagination,
    listFilter?.keyword,
    pagination?.current_page,
    listFilter?.date_filter,
    listFilter?.from,
    pagination?.per_page,
    listFilter?.to,
  ]);

  useEffect(() => {
    handleGetListOrdersPending();
  }, [handleGetListOrdersPending]);

  useEffect(() => {
    const timerID = setInterval(() => {
      handleGetListOrdersPending();
      dispatch(setLoadingOrderPending(false));
    }, Number(systemConfig?.refresh_time ? systemConfig?.refresh_time : 10) * 1000);
    if (pagination?.current_page !== 1) {
      clearInterval(timerID);
    }
    return () => clearInterval(timerID);
  }, [handleGetListOrdersPending, pagination?.current_page, systemConfig?.refresh_time, dispatch]);

  const onSearchKeyWord = (value: string) => {
    dispatch(setKeyword(value));
    dispatch(setCurrentPage(1));
  };

  const handleGetDetailOrder = async (booking_code: string) => {
    try {
      const res = await getOrderDetailAPI({
        booking_code: booking_code,
        ticket_code: '',
      });
      if (res?.status === 200) {
        setOrderData(res?.data?.data);
        setOpenModalOrderInfo(true);
      }
    } catch (error) {
      toast.error('Có lỗi xảy ra vui lòng thử lại!');
    }
  };

  const handleError = (e: any) => {
    e.target.onerror = null;
    e.target.src = logoCompanyDefault;
  };

  const columns: TableProps<OrderItemParams>['columns'] = [
    {
      title: 'STT',
      render: (_, record, index) => (
        <Flex className="Order__column__booking-code" align="center">
          <Flex style={{ width: '36px' }} align="center" justify="center">
            <img height={16} className="object-fit-scale w-auto" src={statusPending} alt="" />
          </Flex>
          <Typography.Text className="fs-16 text-color fw-500 text-nowrap">
            {index + 1 + (pagination?.current_page - 1) * pagination?.per_page}
          </Typography.Text>
        </Flex>
      ),
    },
    {
      title: 'Họ và tên',
      dataIndex: 'customer_name',
      render: (customer_name, record) => (
        <Flex className="flex-column">
          <Flex align="center" className="gap-x-8">
            <img
              width={16}
              height={16}
              className="object-fit-scale"
              src={handleGetGuestSource(record?.guest_source)}
              alt=""
            />
            <Typography.Text className="fs-16 text-color fw-500 text-nowrap">
              <TextDisplay text={customer_name} />
            </Typography.Text>
          </Flex>
          <Typography.Text className="fs-14 text-color-green-default fw-500">
            {handleDisplayTimeToNow(record?.created_at)}
          </Typography.Text>
        </Flex>
      ),
    },
    {
      title: 'Mã đơn hàng',
      dataIndex: 'booking_code',
      render: (booking_code, record) => (
        <Flex className="flex-column">
          <Flex className="gap-x-8" align="center">
            <Typography.Text className="fs-16 text-color fw-500">
              <TextDisplay text={booking_code} />
            </Typography.Text>
            {record?.data?.[0]?.is_insurance && (
              <Tooltip title="Đơn hàng này có bảo hiểm">
                <img width={16} height={16} className="object-fit-scale" src={iconInsurance} alt="" />
              </Tooltip>
            )}
          </Flex>
          <p className="mb-0 text-color-green-default">{record?.amount} vé</p>
        </Flex>
      ),
      sorter: (a, b) => a?.booking_code.localeCompare(b?.booking_code),
    },
    {
      title: 'Chặng',
      dataIndex: '',
      render: (route, record) => (
        <Flex className="gap-x-8" align="center">
          <Tooltip title={record?.company?.name}>
            <img
              onError={handleError}
              src={record?.company_logo}
              width={20}
              height={20}
              className="object-fit-scale"
              alt=""
            />
          </Tooltip>
          <Typography.Text className="fs-16 text-color fw-500">
            {`${record?.segment?.start_location?.name} - ${record?.segment?.end_location?.name}`}
            {record?.round_trip && (
              <img className="object-fit-scale ml-8" width={20} height={20} src={iconSwap} alt="" />
            )}
          </Typography.Text>
        </Flex>
      ),
    },
    {
      title: 'Ngày đi',
      dataIndex: 'depart_date',
      render: (depart_date) => (
        <Typography.Text className="fs-16 text-color fw-500">
          {depart_date ? moment(depart_date).format('DD/MM/YYYY') : ''}
        </Typography.Text>
      ),
      sorter: (a: any, b: any) => moment(a?.depart_date).unix() - moment(b?.depart_date).unix(),
    },
    {
      title: 'Giờ đi',
      dataIndex: 'depart_time',
      render: (depart_time) => (
        <Typography.Text className="fs-16 text-color fw-500">
          {depart_time ? moment(depart_time, 'HH:mm').format('HH:mm') : ''}
        </Typography.Text>
      ),
      sorter: (a, b) => moment(a.depart_time, 'HH:mm').unix() - moment(b.depart_time, 'HH:mm').unix(),
    },
    {
      title: 'Ngày đặt',
      hidden: true,
      dataIndex: 'created_at',
      render: (created_at) => (
        <Typography.Text className="fs-16 text-color fw-500">
          {created_at ? moment(created_at).format('DD/MM/YYYY') : ''}
        </Typography.Text>
      ),
      sorter: (a: any, b: any) => moment(a?.created_at).unix() - moment(b?.created_at).unix(),
    },
    {
      title: 'Nguồn vé',
      hidden: accountUser?.group !== USER_GROUP.CARLINK,
      dataIndex: 'source',
      render: (source) => (
        <Typography.Text className="fs-16 text-color fw-500">{source ? source : 'N/A'}</Typography.Text>
      ),
    },
    {
      title: 'Thanh toán',
      dataIndex: 'payment_method',
      render: (payment_method) => (
        <Typography.Text className="fs-16 text-color fw-500">
          {payment_method === 'momo' ? (
            <Typography.Text className="fs-16 text-color fw-500"> Momo </Typography.Text>
          ) : payment_method === 'vn_pay' ? (
            <Typography.Text className="fs-16 text-color fw-500"> vnpay </Typography.Text>
          ) : payment_method === 'bank_transfer' ? (
            <Flex justify="center" align="center">
              <img width={78} height={16} className="object-fit-scale" src={logoNapas} alt="" />
            </Flex>
          ) : payment_method === 'paid_at_company' ? (
            <Typography.Text className="fs-16 text-color fw-500"> Thanh toán tại nhà xe </Typography.Text>
          ) : (
            <Typography.Text className="fs-16 text-color fw-500"> Thanh toán sau </Typography.Text>
          )}
        </Typography.Text>
      ),
    },
    {
      title: '',
      dataIndex: 'os',
      align: 'right',
      render: (os) => (
        <Typography.Text className="fs-16 text-color fw-500">
          {os === 'web' ? <img src={iconWeb} alt="" /> : <img src={iconMobile} alt="" />}
        </Typography.Text>
      ),
    },
    {
      title: 'Tổng tiền',
      dataIndex: 'total_price',
      align: 'right',
      render: (total_price) => (
        <Typography.Text className="fs-16 text-color fw-500">{total_price ? total_price : 'N/A'}</Typography.Text>
      ),
      sorter: (a, b) => {
        return Number(a?.total_price) - Number(b?.total_price);
      },
    },
    {
      title: 'Chức năng',
      dataIndex: 'booking_code',
      align: 'center',
      render: (booking_code) => (
        <Flex
          onClick={() => handleGetDetailOrder(booking_code)}
          className="Order__column__action"
          align="center"
          justify="center"
        >
          <img width={24} height={24} className="object-fit-scale" src={iconAction} alt="" />
        </Flex>
      ),
    },
  ];

  const handleExportExcel = async () => {
    try {
      setLoadingExcel(true);
      const res = await getListOrdersAPI({
        keyword: listFilter?.keyword ? listFilter?.keyword : '',
        date_filter: listFilter?.date_filter,
        from: dayjs(listFilter?.from, 'YYYY-MM-DD', true).isValid()
          ? dayjs(listFilter?.from).format('YYYY-MM-DD')
          : undefined,
        to: dayjs(listFilter?.to, 'YYYY-MM-DD', true).isValid()
          ? dayjs(listFilter?.to).format('YYYY-MM-DD')
          : undefined,
        status: TabStatusOrderProcessed.UN_PROCESSED,
        is_full: '1',
        is_detail_booking: '0',
      });
      if (isArray(res?.data?.data)) {
        const listData = res?.data?.data;
        const listDataExcel =
          listData?.length > 0
            ? listData?.map((order: OrderItemParams, index: number) => ({
                key: index + 1,
                booking_code: order?.booking_code ? order?.booking_code : 'N/A',
                customer_name: order?.customer_name ? order?.customer_name : 'N/A',
                route: order?.route ? `${order?.route?.depart_point} - ${order?.route?.arrive_point}` : 'N/A',
                depart_date: order?.depart_date ? `${moment(order?.depart_date).format('DD/MM/YYYY')}` : 'N/A',
                depart_time: order?.depart_time ? `${moment(order?.depart_time, 'HH:mm').format('HH:mm')}` : 'N/A',
                created_at: order?.created_at ? `${moment(order?.created_at).format('DD/MM/YYYY')}` : 'N/A',
                payment_method: order?.payment_method === 'bank_transfer' ? 'Chuyển khoản' : 'N/A',
                source: order?.source ? order?.source : 'N/A',
                amount: order?.amount ? Number(order?.amount) : 0,
                total_price: order?.total_price ? Number(order?.total_price) : 0,
              }))
            : [];
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Đơn hàng chưa xử lý');
        const header = columnsExcelPending.map((col) => ({
          header: col.title,
          key: col.dataIndex as string,
        }));
        worksheet.columns = header;
        columnsExcelPending.forEach((col, index) => {
          worksheet.getColumn(index + 1).width = col.width || 15;
          worksheet.getColumn(index + 1).alignment = {
            horizontal: (col?.__style__?.h as 'center' | 'left' | 'right' | 'fill' | 'justify') || 'left',
            vertical: 'middle',
          };
          worksheet.getColumn(index + 1).numFmt = col.__numFmt__ || 'general';
        });
        worksheet.addRows(listDataExcel);
        worksheet.getRow(1).font = {
          name: 'Times New Roman',
          size: 12,
          bold: true,
        };
        worksheet.getRow(1).eachCell((cell, colNumber) => {
          if (colNumber < 12) {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: '3AB54A' },
            };
            cell.border = {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' },
            };
          }
        });
        let firstRowHasData = false;
        worksheet.getRow(1).eachCell((cell, colNumber) => {
          if (cell.value) {
            firstRowHasData = true;
            return;
          }
        });
        if (firstRowHasData) {
          worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
            if (rowNumber > 1) {
              row.eachCell((cell) => {
                cell.font = { name: 'Times New Roman', size: 12 };
                cell.border = {
                  top: { style: 'thin' },
                  left: { style: 'thin' },
                  bottom: { style: 'thin' },
                  right: { style: 'thin' },
                };
              });
            }
          });
        } else {
          worksheet.eachRow((row, rowNumber) => {
            if (rowNumber > 1) {
              let hasData = false;
              row.eachCell((cell) => {
                if (cell.value) {
                  hasData = true;
                  return;
                }
              });
              if (hasData) {
                row.border = {
                  top: { style: 'thin' },
                  left: { style: 'thin' },
                  bottom: { style: 'thin' },
                  right: { style: 'thin' },
                };
              }
            }
          });
        }

        const lastRow = worksheet.addRow([]);
        lastRow.getCell(1).value = '@Bản quyền thuộc nhà xe Carlink';
        lastRow.getCell(1).alignment = {
          vertical: 'middle',
          horizontal: 'left',
        };
        lastRow.getCell(1).font = {
          name: 'Times New Roman',
          size: 14,
          bold: true,
        };
        const endColumnIndex = 0 + columnsExcelPending.length;
        worksheet.mergeCells(lastRow.number, 1, lastRow.number, endColumnIndex);

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Đơn hàng chưa xử lý.xlsx';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
      setLoadingExcel(false);
    } catch (error) {
      setLoadingExcel(false);
      toast.error('Có lỗi xảy ra vui lòng thử lại!');
    }
  };

  const onChangePage = (page: number, pageSize: number) => {
    if (pagination?.per_page !== pageSize) {
      dispatch(setCurrentPage(Constant.DEFAULT_PAGE));
      dispatch(setPerPage(pageSize));
    } else {
      dispatch(setCurrentPage(page));
    }
  };

  const onFinish = (values: FieldType) => {
    dispatch(
      setFilter({
        keyword: values?.keyword ? values?.keyword : '',
        date_filter: values?.date_filter,
        from: values?.range_date_filter?.[0] ? values?.range_date_filter?.[0] : '',
        to: values?.range_date_filter?.[1] ? values?.range_date_filter?.[1] : '',
      }),
    );
    dispatch(setCurrentPage(1));
  };

  return (
    <div className="Order">
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          keyword: listFilter?.keyword ? listFilter?.keyword : '',
          date_filter: listFilter?.date_filter ? listFilter?.date_filter : 'depart_date',
          range_date_filter: [
            listFilter?.from ? dayjs(listFilter?.from, 'DD/MM/YYYY') : undefined,
            listFilter?.to ? dayjs(listFilter?.to, 'DD/MM/YYYY') : undefined,
          ],
        }}
      >
        <Flex className="Order__form" align="start" justify="space-between">
          <Flex className="Order__form" align="center">
            <div className="Order__keyword">
              <Form.Item<FieldType> name="keyword">
                <Input.Search
                  onSearch={onSearchKeyWord}
                  placeholder="Nhập mã đơn hàng để tìm kiếm"
                  suffix={<img className="object-fit-scale" width={16} height={16} src={iconSearch} alt="" />}
                />
              </Form.Item>
            </div>
            <div className="Order__dateFilter">
              <Form.Item<FieldType>
                name="date_filter"
                rules={[{ required: true, message: 'Vui lòng chọn khoảng thời gian' }]}
              >
                <Select placeholder="Theo ngày khởi hành" options={optionsDateFilter} />
              </Form.Item>
            </div>
            <div className="Order__rangeDate">
              <Form.Item<FieldType> name="range_date_filter">
                <RangePicker
                  disabledDate={disabledDaysDate}
                  format="DD/MM/YYYY"
                  popupClassName="Order__rangeDate__picker"
                  placeholder={['Từ ngày', 'Đến ngày']}
                />
              </Form.Item>
            </div>
            <ConfigProvider theme={{ token: { colorPrimary: '#435869' } }}>
              <Form.Item>
                <Button
                  ghost
                  className="Order__exportExcel d-flex align-items-center h-auto"
                  type="primary"
                  htmlType="submit"
                >
                  <img className="object-fit-scale" width={16} height={16} src={iconSearch} alt="" />
                  Xem
                </Button>
              </Form.Item>
            </ConfigProvider>
            <ConfigProvider theme={{ token: { colorPrimary: '#435869' } }}>
              <Form.Item>
                <Button
                  ghost
                  className="Order__exportExcel d-flex align-items-center h-auto"
                  type="primary"
                  loading={loadingExcel}
                  onClick={handleExportExcel}
                >
                  <IconExportExcel color={'#435869'} width={16} height={16} />
                  Xuất Excel
                </Button>
              </Form.Item>
            </ConfigProvider>
          </Flex>
          <Flex align="center" className="Order__tutorials">
            <TutorialsVideo url_youtube={handleGetUrlGuide(listTutorials, 8)} />
          </Flex>
        </Flex>
      </Form>
      <Table
        dataSource={listOrder?.length > 0 ? listOrder : []}
        locale={{
          triggerDesc: 'Sắp xếp tăng dần',
          triggerAsc: 'Hủy sắp xếp',
          cancelSort: 'Sắp xếp giảm dần',
        }}
        loading={loading}
        pagination={{
          total: pagination?.total,
          current: pagination?.current_page,
          pageSize: pagination?.per_page ?? DEFAULT_PAGE_SIZE,
          position: ['bottomRight'],
          onChange: onChangePage,
          showSizeChanger: true,
        }}
        className="Order__table"
        columns={columns}
      />
      <ModalOrderInfo
        typeModal={TypeModalOrder.PENDING}
        orderData={orderData}
        openModalOrderData={openModalOrderInfo}
        setOpenModalOrderData={setOpenModalOrderInfo}
        getDetailOrder={() => handleGetDetailOrder(orderData?.booking_code)}
      />
    </div>
  );
};

export default OrderPending;
